import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Repeat, TrendingUp, ShieldCheck, ArrowUpRight, ArrowLeft, Rocket } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { api } from '@/api';
import { TID } from '@/testIds';

export default function AftercarePage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    api.get('/aftercare/plans')
      .then(({ data }) => setPlans(data.plans))
      .catch(() => toast.error('Could not load aftercare plans'));
  }, []);

  const enroll = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post('/payments/checkout', {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start enrollment');
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-28 pb-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <Link to="/" className="mi-eyebrow text-white/50 hover:text-white inline-flex items-center gap-2 mb-8">
            <ArrowLeft className="w-3 h-3" /> BACK TO HOME
          </Link>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <div className="mi-eyebrow mb-3 flex items-center gap-2 text-[#E63946]">
                <Repeat className="w-3 h-3" /> // AFTERCARE MRR · B2B2C
              </div>
              <h1 className="mi-headline text-5xl md:text-6xl lg:text-7xl">
                Turn every wrap into<br />
                <span className="text-[#E63946]">recurring revenue.</span>
              </h1>
              <p className="mt-6 text-white/75 leading-relaxed">
                You enroll your retail client into an aftercare plan. They pay you monthly. We handle the visits.
                Motor iNKS auto-bills you at wholesale — you keep the spread every month, for the life of the wrap.
              </p>
            </div>
            <Link to="/rates" className="mi-btn-ghost">
              Full rate card <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          {/* The math */}
          <div className="mi-surface p-6 lg:p-8 mb-14 border-l-4 border-l-[#E63946]">
            <div className="flex items-start gap-4 mb-4">
              <TrendingUp className="w-7 h-7 text-[#E63946] flex-shrink-0" />
              <div>
                <div className="mi-eyebrow mb-1 text-[#E63946]">// THE MRR MATH</div>
                <div className="mi-headline text-2xl">Enroll 30 vans on Pro Shield Quarterly.</div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 font-mono-data text-sm">
              <MRRTile k="Your retail sell (30 × $100/mo)" v="$3,000 / mo" />
              <MRRTile k="Our wholesale bill (30 × $60/mo)" v="$1,800 / mo" />
              <MRRTile k="Pure MRR to your shop"           v="$1,200 / mo · $14.4K yr" strong />
            </div>
          </div>

          {/* Plans */}
          <div className="grid md:grid-cols-3 gap-4 mb-14">
            {plans.map((p) => (
              <div
                key={p.id}
                data-testid={TID.aftercareCard(p.id)}
                className={`relative p-8 border ${
                  p.highlight ? 'border-[#E63946] bg-[#E63946]/[0.03]' : 'mi-line-strong bg-[#0A0A0A]'
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 bg-[#E63946] text-white px-3 py-1.5 font-mono-data uppercase tracking-widest text-[10px] font-bold">
                    <Rocket className="w-3 h-3" /> BEST MRR VALUE
                  </div>
                )}
                <div className="mi-eyebrow text-[#E63946]">// {p.sku}</div>
                <div className="mi-headline text-3xl mt-2 mb-2 leading-tight">{p.name}</div>
                <div className="mi-eyebrow text-white/50 mb-4">{p.cadence}</div>

                <div className="flex items-baseline gap-2 mb-1">
                  <span className="mi-headline text-5xl">${p.wholesale_price}</span>
                  <span className="text-white/50 text-sm">/ mo wholesale</span>
                </div>
                <div className="text-xs mi-eyebrow text-white/60 mb-6">
                  RETAIL RANGE · {p.retail_range} / MO
                </div>

                <button
                  onClick={() => enroll(p.lookup_key)}
                  disabled={loading === p.lookup_key}
                  data-testid={TID.aftercareCta(p.id)}
                  className={`w-full ${p.highlight ? 'mi-btn-red' : 'mi-btn-ghost'} disabled:opacity-60`}
                >
                  {loading === p.lookup_key ? 'Starting…' : 'Enroll a wrap'}
                </button>

                <ul className="mt-6 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-white/85">
                      <Check className="w-4 h-4 text-[#E63946] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Notes */}
          <div className="grid md:grid-cols-2 gap-4 mb-14">
            <NotePanel title="How the B2B2C loop works" bullets={[
              'You add aftercare to any wrap proposal as a monthly line item.',
              'Client agrees — you enroll them at your retail price ($95–$135/mo).',
              'Motor iNKS auto-bills you at wholesale ($30–$75/mo per SKU).',
              'You keep the spread, monthly, for the life of the wrap.',
              'We handle visit scheduling, service delivery and photo reports.',
            ]} />
            <NotePanel title="What each visit covers" bullets={[
              'Pre-wash pH neutralization to break down bonded contaminants.',
              'Edge & seam audit — repair pass if lift is detected under 25 psi.',
              'Polymer top-coat (or ceramic quartz on Rejuvenation Annual).',
              'Photo condition report emailed to you and your client.',
              'Full warranty extends as long as the aftercare plan is active.',
            ]} />
          </div>

          {/* Trust */}
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-t mi-line-strong pt-6 text-xs mi-eyebrow text-white/50">
            <span className="flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> SECURED BY STRIPE</span>
            <span>MONTHLY BILLING · CANCEL ANY TIME</span>
            <span>PROOF-OF-VISIT PHOTOS INCLUDED</span>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function MRRTile({ k, v, strong }) {
  return (
    <div className={`p-4 border ${strong ? 'border-[#E63946] bg-[#E63946]/[0.08]' : 'mi-line'}`}>
      <div className="mi-eyebrow text-[10px] text-white/60 mb-2">{k.toUpperCase()}</div>
      <div className={`mi-headline text-2xl ${strong ? 'text-white' : 'text-white/85'}`}>{v}</div>
    </div>
  );
}

function NotePanel({ title, bullets }) {
  return (
    <div className="mi-surface p-5">
      <div className="mi-eyebrow mb-3 text-[#E63946]">// {title.toUpperCase()}</div>
      <ul className="space-y-2 text-sm text-white/80">
        {bullets.map((b) => (
          <li key={b} className="flex gap-2"><span className="text-[#E63946]">→</span><span>{b}</span></li>
        ))}
      </ul>
    </div>
  );
}
